/*
 * SectionNavigation
 * @module components
 * @flow weak
 */

import React from 'react';
import { compose, lifecycle, pure, withHandlers, withProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import * as R from 'ramda';

import { withDevice } from '~/features/App/Device';
import { withSettings } from '~/features/App/Settings';
import { matchRouteByPathname, scrollTabs } from '~/helpers';

import { SectionNavigation as SNV } from './SectionNavigation';

const SCROLL_NAVIGATION_DELAY = 100;

const findTabIndexRecursive = (pathname, tabs) =>
  R.findIndex(({ route, tabs }) =>
    tabs
      ? findTabIndexRecursive(pathname, tabs) !== -1
      : matchRouteByPathname(pathname, route),
  )(tabs);

const prepareTabs = ({ routes, isSubTabs }) =>
  R.pipe(
    R.values,
    R.map(({ link, route, routes }) => ({
      link,
      route,
      // $FlowIssue
      ...(!isSubTabs ? { ref: React.createRef() } : null),
      ...(routes ? { tabs: prepareTabs({ routes, isSubTabs: true }) } : null),
    })),
  )(routes);

const enhance = compose(
  withDevice,
  withRouter,
  withSettings,
  withProps(({ routes }) => ({
    navRef: React.createRef(),
    scrollRef: React.createRef(),
    tabs: prepareTabs({ routes }),
  })),
  withHandlers({
    scrollNavigation: ({
      device,
      location: { pathname },
      navRef,
      scrollRef,
      tabs,
    }) => () => {
      if (device === 'desktop') {
        return;
      }

      scrollTabs({
        index: findTabIndexRecursive(pathname, tabs),
        navRef,
        scrollRef,
        tabs,
      });
    },
  }),
  lifecycle({
    componentDidMount() {
      this.props.scrollNavigation();
    },
    componentDidUpdate() {
      const { navRef, scrollNavigation } = this.props;
      const isNavigationReady = !!(
        navRef.current && navRef.current.offsetWidth
      );

      if (isNavigationReady) {
        scrollNavigation();
      } else {
        // NOTE: in some cases scroll navigation dom node is not ready therefore added delay
        setTimeout(() => scrollNavigation(), SCROLL_NAVIGATION_DELAY);
      }
    },
  }),
  pure,
);

export const SectionNavigation = enhance(SNV);
